
import {defineComponent} from 'vue';
import Icon from '@ohrm/oxd/core/components/Icon/Icon.vue';
import usei18n from '@ohrm/oxd/composables/usei18n';
import Label from '@ohrm/oxd/core/components/Label/Label.vue';

export interface State {
  focused: boolean;
  inputValue: string;
}

export default defineComponent({
  name: 'FileInput',

  components: {
    'oxd-icon': Icon,
    'oxd-label': Label,
  },
  inheritAttrs: false,

  props: {
    modelValue: {},
    style: {
      type: Object,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
    },
    buttonIcon: {
      type: String,
      default: 'upload',
    },
    placeholder: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'File',
    },
    hasLabel: {
      type: Boolean,
      default: true,
    },
    isSingle: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click', 'focus', 'blur', 'input', 'update:modelValue'],

  setup() {
    return {
      ...usei18n(),
    };
  },

  data(): State {
    return {
      focused: false,
      inputValue: '',
    };
  },

  computed: {
    classes(): object {
      return {
        'oxd-file-div': true,
        'oxd-file-div--active': !this.focused,
        'oxd-file-div--focus': this.focused,
        'oxd-file-div--error': this.hasError,
        'oxd-file-div--disabled': this.disabled,
        'oxd-file-div--readonly': this.readonly,
      };
    },
    fileInputClasses(): object {
      return {
        'oxd-file-input': true,
      };
    },
    placeholderText(): string {
      return (
        this.placeholder ?? this.t('general.no_file_chosen', 'No file chosen')
      );
    },
    labelText(): string {
      if (this.required) return `${this.label}*`;

      return this.label;
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue !== undefined && newValue !== null) {
          this.inputValue = newValue.name;
        } else {
          this.inputValue = '';
        }
      }
    },
  },

  methods: {
    onClick(e: Event) {
      if (this.disabled || this.readonly) return;
      const inputRef = this.$refs.input as HTMLInputElement;
      inputRef.focus();
      inputRef.click();
      this.$emit('click', e);
    },
    onFocus(e: Event) {
      this.focused = true;
      this.$emit('focus', e);
    },
    onBlur(e: Event) {
      this.focused = false;
      this.$emit('blur', e);
    },
    onInput(e: Event) {
      e.preventDefault();
      const files = [...((e.target as HTMLInputElement).files || [])];
      const inputValue = files.map((file: File) => file.name).join(', ');

      if (files.length > 0) {
        // Not supporting with multiple (i.e. <input type="file" multiple>)
        const reader = new FileReader();

        reader.onload = (event: ProgressEvent<FileReader>) => {
          if (
            typeof event.target?.result === 'string' ||
            event.target?.result instanceof String
          ) {
            this.inputValue = inputValue;
          }
        };

        this.$emit('update:modelValue', files[0]);
        reader.readAsDataURL(files[0]);
      } else {
        this.inputValue = inputValue;
        this.$emit('update:modelValue', null);
      }

      this.$emit('input', e);
    },
  },
});
